import React from 'react';
import { motion } from 'framer-motion';
import { LockIcon, SparklesIcon } from 'lucide-react';
import { DjaySpecimen } from '../../../mascot';
import type { DjayVariant } from '../../../mascot';
import { VariantUnlockInfo } from '../../../mascot/useVariantUnlocks';
import { RADIUS } from '../../../config/designTokens';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
interface GhostTrophyProps {
  /** The next collectible variant the learner is working toward */
  variant: VariantUnlockInfo;
  align: 'left' | 'right' | 'center';
  /** Deep-link into the Collection (trophy room) */
  onOpenCollection: () => void;
}
/**
 * Inline "ghost-trophy" marker on the Voortgang journey.
 *
 * Bridges live growth and the permanent collectible reward: it teases the
 * NEXT unlockable Djay variant as a faded, locked silhouette with a friendly
 * "nog X te gaan" goal + progress bar. Tapping it deep-links to the variant's
 * card in the Collection. Purely motivational — it never duplicates the reward
 * itself, it just shows what's coming.
 */
export function GhostTrophy({
  variant,
  align,
  onOpenCollection
}: GhostTrophyProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    progress
  } = variant;
  const pct = progress.max > 0 ? Math.min(100, Math.round(progress.current / progress.max * 100)) : 0;
  const remaining = Math.max(0, progress.max - progress.current);
  const alignClass = align === 'left' ? 'self-start ml-4 sm:ml-12' : align === 'right' ? 'self-end mr-4 sm:mr-12' : 'self-center';
  return <motion.button type="button" onClick={() => {
    sessionStorage.setItem('collectionFocus', variant.id);
    onOpenCollection();
  }} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`relative z-10 flex items-center gap-4 w-full max-w-[280px] sm:max-w-[320px] p-3 sm:p-4 bg-white/90 border-4 border-dashed border-amber-200 ${RADIUS.card} shadow-md hover:shadow-lg transition-shadow text-left ${alignClass}`} aria-label={`Volgende beloning: ${variant.name}. Nog ${remaining} ${progress.label} te gaan. Open de collectie.`} data-id="element-1895" data-mp="1pjljvqmr3tsm">
      {/* Faded, locked Djay silhouette in the target variant's colours */}
      <div className="relative flex-shrink-0" data-id="element-1896" data-mp="35xvtmxcr07ke">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-amber-50/60 border-2 border-amber-100" data-id="element-1897" data-mp="35xm41avm82f8">
          <motion.div className="opacity-30 grayscale-[35%]" animate={shouldAnimate ? {
          y: [0, -3, 0],
          opacity: [0.28, 0.38, 0.28]
        } : undefined} transition={{
          repeat: Infinity,
          duration: 3,
          ease: 'easeInOut'
        }} data-id="element-1898" data-mp="2e6y5m6lbtb59">
            <DjaySpecimen size="md" look={{
            variant: variant.id as DjayVariant
          }} emotion="happy" animate={false} data-id="element-1899" data-mp="2gdo4wkg7cn5" />
          </motion.div>
        </div>
        <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-amber-100" data-id="element-1900" data-mp="7olg9o5nzs86">
          <LockIcon className="w-3.5 h-3.5 text-amber-500" strokeWidth={2.5} data-id="element-1901" data-mp="3uzbfxb9cfbh3" />
        </div>
      </div>

      {/* Teaser copy + progress */}
      <div className="flex-1 min-w-0" data-id="element-1902" data-mp="3q11gupkhzqq7">
        <p className="text-[10px] sm:text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1" data-id="element-1903" data-mp="3pxvrd97yimty">
          <SparklesIcon className="w-3 h-3" data-id="element-1904" data-mp="3g1sou2970m6h" /> Volgende beloning
        </p>
        <h3 className="text-sm sm:text-base font-black text-slate-800 leading-tight truncate" data-id="element-1905" data-mp="3g3vqjjjpk3r1">
          {variant.name}
        </h3>
        <p className="text-[11px] sm:text-xs font-bold text-slate-500 mb-1.5" data-id="element-1906" data-mp="1eb7hgtkdum3c">
          Nog {remaining} {progress.label} te gaan!
        </p>
        <div className="h-2 w-full bg-amber-100 rounded-full overflow-hidden" data-id="element-1907" data-mp="1a9m291kdlvpz">
          <motion.div className="h-full bg-amber-400 rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${pct}%`
        }} transition={{
          duration: shouldAnimate ? 0.6 : 0,
          ease: 'easeOut'
        }} data-id="element-1908" data-mp="1hdls6tibj5hj" />
        </div>
      </div>
    </motion.button>;
}